import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStyles, useColors } from "../ui";
import { useRpc, useCommand } from "../store";
import { mapBridge } from "../map-bridge";
import { WebMap } from "../web-map";
import { triageColorHex } from "../triage";
import { uuidv7 } from "../../../shared/src/ids";
import { useAuth } from "../auth";

// ─── Types ────────────────────────────────────────────────────────────

interface FieldEventSummary {
  id: string;
  startedAt: number;
  stoppedAt: number | null;
  unitName: string | null;
  leadMedicName: string | null;
}

interface SoldierEventRow {
  id: string;
  type: string;
  timestamp: number;
  gpsLat: number | null;
  gpsLon: number | null;
  payload: Record<string, any>;
}

interface SoldierDetail {
  soldierId: string | null;
  soldierName: string | null;
  watchId: string;
  lastLat: number | null;
  lastLon: number | null;
  lastTriageLevel: string | null;
  events: SoldierEventRow[];
}

interface FieldEventDetail {
  id: string;
  startedAt: number;
  stoppedAt: number | null;
  unitName: string | null;
  leadMedicName: string | null;
  soldiers: SoldierDetail[];
}

// ─── Helpers ──────────────────────────────────────────────────────────

function formatDateTime(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatDuration(startMs: number, stopMs: number | null): string {
  const endMs = stopMs ?? Date.now();
  const diffS = Math.floor((endMs - startMs) / 1000);
  const h = Math.floor(diffS / 3600);
  const m = Math.floor((diffS % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// triageColorHex imported from ../triage — fallback blue for "no triage assigned"
const triageColor = (level: string | null) => triageColorHex(level, "#2980b9");

function describeEvent(row: SoldierEventRow): string {
  const type = row.type.toUpperCase();
  const p = row.payload?.payload ?? row.payload ?? {};
  const parts: string[] = [];

  if (type === "PAIR" || type === "WATCHEVENT_PAIR") {
    return "Watch paired";
  }
  if (type === "UNPAIR") {
    return "Watch unpaired";
  }
  if (type === "TRIAGEALERT") {
    if (p.triageLevel) parts.push(p.triageLevel);
    if (p.hrBpm != null) parts.push(`HR ${p.hrBpm}`);
    if (p.spo2Pct != null) parts.push(`SpO₂ ${p.spo2Pct}%`);
    if (p.batteryPct != null) parts.push(`Bat ${p.batteryPct}%`);
    return parts.join(" · ") || "Triage alert";
  }
  if (type === "MANDOWN") {
    return "Man Down";
  }
  if (type === "STATUSRESPONSE") {
    if (p.hrBpm != null) parts.push(`HR ${p.hrBpm}`);
    if (p.spo2Pct != null) parts.push(`SpO₂ ${p.spo2Pct}%`);
    if (p.batteryPct != null) parts.push(`Bat ${p.batteryPct}%`);
    return parts.join(" · ") || "Status ping";
  }
  if (type === "ASSIGNED") {
    return `Assigned to ${p.medicName ?? "medic"}`;
  }
  if (type === "TRIAGE_COMPLETE") {
    return `Triage completed by ${p.medicName ?? "medic"}`;
  }
  if (type === "CASE_CREATED") {
    const cat = p.triageCategory ? ` · T${["I","II","III","IV"].indexOf(p.triageCategory) + 1}` : "";
    return `Case created${cat}${p.medicName ? ` by ${p.medicName}` : ""}`;
  }
  return type.toLowerCase().replace("_", " ");
}

function eventBadgeColor(type: string): string {
  const t = type.toUpperCase();
  if (t === "TRIAGEALERT") return "#c0392b";
  if (t === "MANDOWN") return "#e67e22";
  if (t === "PAIR") return "#27ae60";
  if (t === "ASSIGNED") return "#f39c12";
  if (t === "TRIAGE_COMPLETE") return "#27ae60";
  if (t === "CASE_CREATED") return "#2980b9";
  return "#7f8c8d";
}

// ─── Map helpers ──────────────────────────────────────────────────────

function placeHistoricalMarkers(soldiers: SoldierDetail[]) {
  mapBridge.clearMarkers();
  for (const s of soldiers) {
    if (s.lastLat == null || s.lastLon == null) continue;
    const name = s.soldierName ?? s.watchId;
    const remarks = s.lastTriageLevel ? `${s.lastTriageLevel} · Historical` : "Historical position";
    mapBridge.addMarker({ uid: s.watchId, name, lat: s.lastLat, lon: s.lastLon, remarks, triageLevel: s.lastTriageLevel ?? "" });
  }
}

// ─── Soldier row (expandable) ─────────────────────────────────────────

function focusEventOnMap(soldier: SoldierDetail, ev: SoldierEventRow) {
  if (ev.gpsLat == null || ev.gpsLon == null) return;
  const name = soldier.soldierName ?? soldier.watchId;
  const p = ev.payload?.payload ?? ev.payload ?? {};
  const type = ev.type.toUpperCase();
  let triageLevel = "";
  if (type === "TRIAGEALERT") triageLevel = p.triageLevel ?? "";
  if (type === "MANDOWN") triageLevel = "T1";
  mapBridge.addMarker({ uid: soldier.watchId, name, lat: ev.gpsLat, lon: ev.gpsLon, remarks: describeEvent(ev), triageLevel });
  mapBridge.focusMarker(soldier.watchId);
}

function SoldierRow({ soldier, fieldEventId }: { soldier: SoldierDetail; fieldEventId: string }) {
  const styles = useStyles();
  const colors = useColors();
  const navigate = useNavigate();
  const dispatch = useCommand();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [localCaseId, setLocalCaseId] = useState<string | null>(null);
  const color = triageColor(soldier.lastTriageLevel);
  const name = soldier.soldierName ?? soldier.watchId;

  // Find an existing case from the injected CASE_CREATED event
  const existingCaseId = localCaseId
    ?? soldier.events.find((e) => e.type.toUpperCase() === "CASE_CREATED")?.payload?.caseId
    ?? null;

  const handleCreateCase = useCallback(() => {
    if (!soldier.soldierId) return;
    const caseUuid = uuidv7();
    setLocalCaseId(caseUuid);
    navigate(`/cases/${caseUuid}`);
    dispatch("case.create", { uuid: caseUuid, fieldEventId });
    dispatch("case.setPatient", { uuid: caseUuid, patientUuid: soldier.soldierId });
    if (soldier.lastTriageLevel) {
      const cat = { T1: "I", T2: "II", T3: "III", T4: "IV", T0: "IV" }[soldier.lastTriageLevel];
      if (cat) dispatch("case.setTriageCategory", { uuid: caseUuid, triageCategory: cat as any });
    }
    if (user?.name) dispatch("case.setMedicName", { uuid: caseUuid, name: user.name });
  }, [soldier, fieldEventId, dispatch, navigate, user]);

  return (
    <>
      <tr
        onClick={() => setExpanded((v) => !v)}
        style={{ cursor: "pointer", borderLeft: `3px solid ${color}` }}
        onMouseOver={(e) => { e.currentTarget.style.background = colors.bgAlt; }}
        onMouseOut={(e) => { e.currentTarget.style.background = ""; }}
      >
        <td style={{ ...styles.td, fontWeight: 600, color }}>
          {soldier.lastTriageLevel ?? "—"}
        </td>
        <td style={styles.td}>{name}</td>
        <td style={{ ...styles.td, color: colors.textMuted, fontSize: "0.8rem" }}>
          {soldier.events.length} event{soldier.events.length !== 1 ? "s" : ""}
        </td>
        <td style={{ ...styles.td, color: colors.textMuted, fontSize: "0.8rem" }}>
          {soldier.lastLat != null
            ? `${soldier.lastLat.toFixed(4)}, ${soldier.lastLon!.toFixed(4)}`
            : "No GPS"}
        </td>
        <td style={{ ...styles.td, textAlign: "right", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
          {existingCaseId ? (
            <button
              style={{ ...styles.btn, fontSize: "0.75rem", background: "#2980b9", color: "#fff", border: "none", marginRight: "6px" }}
              onClick={() => navigate(`/cases/${existingCaseId}`)}
            >
              Go to Case
            </button>
          ) : soldier.soldierId ? (
            <button
              style={{ ...styles.btn, fontSize: "0.75rem", marginRight: "6px" }}
              onClick={handleCreateCase}
            >
              Create Case
            </button>
          ) : null}
          <span style={{ color: colors.textMuted }}>{expanded ? "▲" : "▼"}</span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ padding: 0 }}>
            <div style={{
              background: colors.bgAlt,
              borderLeft: `3px solid ${color}`,
              padding: "8px 12px",
            }}>
              {soldier.events.length === 0 && (
                <div style={{ color: colors.textMuted, fontSize: "0.8rem" }}>No events recorded</div>
              )}
              {soldier.events.map((ev) => {
                const hasGps = ev.gpsLat != null;
                const isCaseEvent = ev.type.toUpperCase() === "CASE_CREATED";
                const caseId = isCaseEvent ? ev.payload?.caseId : null;
                const clickable = hasGps || isCaseEvent;
                const handleClick = isCaseEvent && caseId
                  ? () => navigate(`/cases/${caseId}`)
                  : hasGps ? () => focusEventOnMap(soldier, ev) : undefined;
                return (
                  <div
                    key={ev.id}
                    onClick={handleClick}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      padding: "5px 0",
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: "0.8rem",
                      cursor: clickable ? "pointer" : "default",
                    }}
                    onMouseOver={(e) => { if (clickable) e.currentTarget.style.background = colors.bg; }}
                    onMouseOut={(e) => { if (clickable) e.currentTarget.style.background = ""; }}
                  >
                    <span style={{
                      flexShrink: 0,
                      background: eventBadgeColor(ev.type),
                      color: "#fff",
                      borderRadius: "3px",
                      padding: "1px 6px",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                    }}>
                      {ev.type.toUpperCase()
                        .replace("TRIAGEALERT", "TRIAGE")
                        .replace("MANDOWN", "MAN DOWN")
                        .replace("TRIAGE_COMPLETE", "COMPLETE")
                        .replace("CASE_CREATED", "CASE")
                        .replace("STATUSRESPONSE", "STATUS")}
                    </span>
                    <span style={{ flex: 1, color: colors.text }}>{describeEvent(ev)}</span>
                    <span style={{ flexShrink: 0, color: colors.textMuted }}>
                      {formatDateTime(ev.timestamp)}
                    </span>
                    {isCaseEvent ? (
                      <span style={{ flexShrink: 0, color: "#2980b9" }}>Open case ↗</span>
                    ) : hasGps ? (
                      <span style={{ flexShrink: 0, color: clickable ? "#2980b9" : colors.textMuted }}>
                        {ev.gpsLat!.toFixed(4)}, {ev.gpsLon!.toFixed(4)}
                        {clickable && " ↗"}
                      </span>
                    ) : (
                      <span style={{ flexShrink: 0, color: colors.textMuted, fontStyle: "italic" }}>no GPS</span>
                    )}
                  </div>
                );
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Detail view ──────────────────────────────────────────────────────

function EventDetail({
  eventId,
  onBack,
}: {
  eventId: string;
  onBack: () => void;
}) {
  const styles = useStyles();
  const colors = useColors();
  const rpc = useRpc();
  const [detail, setDetail] = useState<FieldEventDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clear markers when this detail view unmounts (navigating back or to another event)
  useEffect(() => {
    return () => { mapBridge.clearMarkers(); };
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    rpc<{ detail: FieldEventDetail }>({ type: "getFieldEventDetail", fieldEventId: eventId })
      .then((res) => {
        setDetail(res.detail);
        setLoading(false);
        // Auto-place markers on the web map when the detail loads
        if (!mapBridge.isNative) placeHistoricalMarkers(res.detail.soldiers);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [eventId, rpc]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowOnMap = useCallback(() => {
    if (detail) placeHistoricalMarkers(detail.soldiers);
  }, [detail]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={styles.toolbar}>
          <button style={styles.btn} onClick={onBack}>← Back</button>
          <span style={{ fontWeight: 600 }}>Past Events</span>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: colors.textMuted }}>
          Loading…
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div style={styles.toolbar}>
          <button style={styles.btn} onClick={onBack}>← Back</button>
        </div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: colors.danger }}>
          {error ?? "Event not found"}
        </div>
      </div>
    );
  }

  const withGps = detail.soldiers.filter((s) => s.lastLat != null);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ ...styles.toolbar, gap: "12px" }}>
        <button style={styles.btn} onClick={onBack}>← Back</button>
        <span style={{ fontWeight: 600 }}>
          {formatDateTime(detail.startedAt)}
          {detail.stoppedAt ? ` — ${formatDateTime(detail.stoppedAt)}` : " (active)"}
        </span>
        {detail.unitName && (
          <span style={{ fontSize: "0.8rem", color: colors.textMuted }}>{detail.unitName}</span>
        )}
        <span style={{ flex: 1 }} />
        {withGps.length > 0 && (
          <button style={styles.btn} onClick={handleShowOnMap} title="Place last-known positions on map">
            Show on Map ({withGps.length})
          </button>
        )}
      </div>

      {/* Summary strip */}
      <div style={{
        display: "flex",
        gap: "24px",
        padding: "8px 12px",
        background: colors.bgAlt,
        borderBottom: `1px solid ${colors.border}`,
        fontSize: "0.8rem",
        color: colors.textMuted,
        flexWrap: "wrap",
      }}>
        <span>Duration: <strong style={{ color: colors.text }}>{formatDuration(detail.startedAt, detail.stoppedAt)}</strong></span>
        {detail.leadMedicName && (
          <span>Lead: <strong style={{ color: colors.text }}>{detail.leadMedicName}</strong></span>
        )}
        <span>Soldiers: <strong style={{ color: colors.text }}>{detail.soldiers.length}</strong></span>
        <span style={{ color: detail.stoppedAt ? colors.textMuted : "#27ae60" }}>
          {detail.stoppedAt ? "Ended" : "Active"}
        </span>
      </div>

      {!mapBridge.isNative && withGps.length > 0 && <WebMap height="240px" />}

      <div style={{ flex: 1, overflow: "auto" }}>
        {detail.soldiers.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: colors.textMuted }}>
            No soldiers recorded for this event
          </div>
        ) : (
          <table style={{ ...styles.table, width: "100%" }}>
            <thead>
              <tr>
                <th style={styles.th}>Triage</th>
                <th style={styles.th}>Soldier</th>
                <th style={styles.th}>Events</th>
                <th style={styles.th}>Last Position</th>
                <th style={styles.th} />
              </tr>
            </thead>
            <tbody>
              {detail.soldiers.map((s) => (
                <SoldierRow key={s.watchId} soldier={s} fieldEventId={eventId} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── List view ────────────────────────────────────────────────────────

function EventList({ onSelect }: { onSelect: (id: string) => void }) {
  const styles = useStyles();
  const colors = useColors();
  const rpc = useRpc();
  const [events, setEvents] = useState<FieldEventSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    rpc<{ events: FieldEventSummary[] }>({ type: "listFieldEvents" })
      .then((res) => {
        setEvents(res.events);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [rpc]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.toolbar}>
        <span style={{ fontWeight: 600 }}>Past Events</span>
      </div>

      <div style={{ flex: 1, overflow: "auto" }}>
        {loading && (
          <div style={{ padding: "2rem", textAlign: "center", color: colors.textMuted }}>Loading…</div>
        )}
        {!loading && events.length === 0 && (
          <div style={{ padding: "2rem", textAlign: "center", color: colors.textMuted }}>
            No field events recorded yet
          </div>
        )}
        {!loading && events.length > 0 && (
          <table style={{ ...styles.table, width: "100%" }}>
            <thead>
              <tr>
                <th style={styles.th}>Started</th>
                <th style={styles.th}>Duration</th>
                <th style={styles.th}>Lead Medic</th>
                <th style={styles.th}>Unit</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr
                  key={e.id}
                  onClick={() => onSelect(e.id)}
                  style={{ cursor: "pointer" }}
                  onMouseOver={(ev) => { ev.currentTarget.style.background = colors.bgAlt; }}
                  onMouseOut={(ev) => { ev.currentTarget.style.background = ""; }}
                >
                  <td style={{ ...styles.td, fontWeight: 500 }}>
                    {formatDateTime(e.startedAt)}
                  </td>
                  <td style={styles.td}>
                    {formatDuration(e.startedAt, e.stoppedAt)}
                  </td>
                  <td style={styles.td}>{e.leadMedicName ?? "—"}</td>
                  <td style={styles.td}>{e.unitName ?? "—"}</td>
                  <td style={styles.td}>
                    <span style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: e.stoppedAt ? colors.textMuted : "#27ae60",
                    }}>
                      {e.stoppedAt ? "Ended" : "Active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Page entry point ─────────────────────────────────────────────────

export function PastEventsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (selectedId) {
    return <EventDetail eventId={selectedId} onBack={() => setSelectedId(null)} />;
  }
  return <EventList onSelect={setSelectedId} />;
}
